import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Copy, Check, Share2, Phone, Mail, MessageSquare, Bell, LogOut, Link as LinkIcon, PlayCircle, ChevronLeft, ChevronRight, Save, Send, MapPin, ArrowLeft, Clock } from "lucide-react";
import { Logo } from "../../components/Logo";
import { API, teamApi, teamHeaders, formatErr, buildOnboardUrl, buildOnboardWhatsappHref } from "../../lib/teamApi";
import { formatSlotIst } from "../../lib/calendar";
import { renderTemplate, waPhone } from "../../lib/templateRender";

const LEAD_STAGES = ["New", "Contacted", "Link Sent", "In Progress", "Onboarded", "Not Interested"];
const CALL_STATUSES = ["", "No Answer", "Busy", "Call Later", "Wrong Number", "Interested", "Not Interested"];

const StatusPill = ({ status }) => {
  const map = {
    not_opened: { bg: "bg-slate-100", text: "text-slate-600", label: "Not opened" },
    in_progress: { bg: "bg-[#FFF3ED]", text: "text-[#FF5E1A]", label: "In progress" },
    completed: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Completed" },
  };
  const s = map[status] || map.not_opened;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${s.bg} ${s.text} px-2 py-0.5 text-[11px] font-semibold`}>
      {s.label}
    </span>
  );
};

const CopyBtn = ({ url, testid }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(url); } catch {}
        setCopied(true); setTimeout(() => setCopied(false), 1500);
      }}
      data-testid={testid}
      title={copied ? "Copied!" : "Copy invite link"}
      className="p-2 rounded-lg text-slate-500 hover:text-[#2A5CB8] hover:bg-white"
    >
      {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <LinkIcon className="w-4 h-4" />}
    </button>
  );
};

const Field = ({ label, value, span }) => (
  <div className={span ? "col-span-2" : ""}>
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="text-sm font-semibold text-[#0F172A] mt-0.5 break-words">{value || "—"}</div>
  </div>
);

export default function TeamDashboard() {
  const navigate = useNavigate();
  const me = teamApi.me();
  const [cps, setCps] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [leadIdx, setLeadIdx] = useState(0);
  const [view, setView] = useState("list"); // list (scroll leads) | lead (single CP calling view)
  const [stageSel, setStageSel] = useState("New");
  const [statusSel, setStatusSel] = useState("");
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState([]);
  const [remAt, setRemAt] = useState("");
  const [remNote, setRemNote] = useState("");
  const [inviteProg, setInviteProg] = useState("");
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgProg, setMsgProg] = useState("");
  const [msgTpl, setMsgTpl] = useState("");
  const [msgText, setMsgText] = useState("");
  const [msgCopied, setMsgCopied] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const cp = cps[leadIdx] || null;

  const loadAll = async () => {
    try {
      const [cRes, pRes, rRes, tRes] = await Promise.all([
        axios.get(`${API}/team/cps`, { headers: teamHeaders() }),
        axios.get(`${API}/team/programs`, { headers: teamHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/team/reminders`, { headers: teamHeaders() }),
        axios.get(`${API}/team/message-templates`, { headers: teamHeaders() }).catch(() => ({ data: [] })),
      ]);
      setCps(cRes.data || []);
      setPrograms(pRes.data || []);
      setReminders(rRes.data || []);
      setTemplates(tRes.data || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!teamApi.me()) { navigate("/team/login"); return; }
    loadAll();
  }, [navigate]);

  // Sync local fields when the lead changes
  useEffect(() => {
    if (!cp) return;
    setStageSel(cp.lead_stage || "New");
    setStatusSel(cp.call_status || "");
    setNoteText("");
    setRemAt(""); setRemNote("");
    setInviteProg(programs[0]?.id || "");
    (async () => {
      try {
        const r = await axios.get(`${API}/team/cp/${cp.id}/notes`, { headers: teamHeaders() });
        setNotes(r.data || []);
      } catch { setNotes([]); }
    })();
  }, [leadIdx, cps.length]);

  const updateStatus = async () => {
    if (!cp) return;
    try {
      await axios.put(`${API}/team/cp/${cp.id}/status`, { lead_stage: stageSel, call_status: statusSel }, { headers: teamHeaders() });
      if (noteText.trim()) {
        await axios.post(`${API}/team/cp/${cp.id}/note`, { text: noteText.trim() }, { headers: teamHeaders() });
        setNoteText("");
        const r = await axios.get(`${API}/team/cp/${cp.id}/notes`, { headers: teamHeaders() });
        setNotes(r.data || []);
      }
      setCps((list) => list.map((c, i) => (i === leadIdx ? { ...c, lead_stage: stageSel, call_status: statusSel } : c)));
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) { alert(formatErr(e)); }
  };

  const saveReminder = async () => {
    if (!cp || !remAt) return;
    await axios.post(`${API}/team/cp/${cp.id}/reminder`, { remind_at: remAt, note: remNote }, { headers: teamHeaders() });
    setRemAt(""); setRemNote("");
    const r = await axios.get(`${API}/team/reminders`, { headers: teamHeaders() });
    setReminders(r.data || []);
  };

  const markDone = async (rid) => {
    await axios.put(`${API}/team/reminders/${rid}/done`, {}, { headers: teamHeaders() });
    setReminders((rs) => rs.filter((r) => r.id !== rid));
  };

  const createInvite = async (cpId, programId) => {
    try {
      await axios.post(`${API}/team/invites`, { cp_id: cpId, program_id: programId }, { headers: teamHeaders() });
      const r = await axios.get(`${API}/team/cps`, { headers: teamHeaders() });
      setCps(r.data || []);
      return true;
    } catch (e) { alert(formatErr(e)); return false; }
  };

  // ---- Template message composer ----
  const openComposer = () => {
    setMsgProg(cp?.invites?.[0]?.program_id || programs[0]?.id || "");
    setMsgTpl(templates[0]?.id || "");
    setMsgText("");
    setMsgOpen(true);
  };

  const composeMessage = async (progId, tplId) => {
    if (!cp || !progId || !tplId) return;
    // Ensure an invite exists for the chosen program (idempotent on backend)
    const ok = await createInvite(cp.id, progId);
    if (!ok) return;
    const fresh = await axios.get(`${API}/team/cps`, { headers: teamHeaders() });
    const freshCp = (fresh.data || []).find((c) => c.id === cp.id);
    const inv = (freshCp?.invites || []).find((i) => i.program_id === progId);
    const tpl = templates.find((t) => t.id === tplId);
    const prog = programs.find((p) => p.id === progId);
    setMsgText(renderTemplate(tpl?.body || "", {
      cpName: cp.name, cpCompany: cp.company, cpCity: cp.city, cpPhone: cp.phone, cpEmail: cp.email,
      training: prog?.title || inv?.program_title || "",
      link: inv ? buildOnboardUrl(inv) : "",
      yourName: me?.name || "",
    }));
  };

  useEffect(() => {
    if (msgOpen && msgProg && msgTpl) composeMessage(msgProg, msgTpl);
  }, [msgOpen, msgProg, msgTpl]);

  const digits = waPhone(cp?.phone);

  return (
    <div className="min-h-screen bg-white/60">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3"><Logo /> <span className="font-heading font-bold text-lg">Team CRM</span></div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">Signed in as <b>{me?.name || me?.email}</b></span>
          <button
            onClick={() => { teamApi.logout(); navigate("/team/login"); }}
            data-testid="team-logout"
            className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-white"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid md:grid-cols-3 gap-6">
        {/* Reminders */}
        <aside className="md:col-span-1 order-2 md:order-1">
          <div className="glass-strong rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-[#FF5E1A]" />
              <h3 className="font-bold">Follow-ups</h3>
              <span className="text-xs text-slate-500">({reminders.length})</span>
            </div>
            {reminders.length === 0 && <p className="text-xs text-slate-500">No pending reminders.</p>}
            <ul className="flex flex-col gap-2" data-testid="team-reminders">
              {reminders.map((r) => (
                <li key={r.id} className="rounded-xl bg-white p-3 text-xs border border-slate-100">
                  <div className="font-semibold">{r.cp_name}</div>
                  <div className="text-slate-500">{new Date(r.remind_at).toLocaleString()}</div>
                  {r.note && <div className="text-slate-600 mt-1">{r.note}</div>}
                  <button onClick={() => markDone(r.id)} className="mt-2 text-[11px] text-[#2A5CB8] hover:text-[#FF5E1A] font-semibold">
                    Mark done
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Lead view */}
        <main className="md:col-span-2 order-1 md:order-2">
          {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
          {loading && <div className="text-slate-500">Loading…</div>}
          {!loading && cps.length === 0 && (
            <div className="glass-strong rounded-2xl p-6 text-center text-slate-500">
              No CPs assigned to you yet. Ask admin to assign.
            </div>
          )}

          {/* Stage 1: scrollable lead list (mobile-first) */}
          {view === "list" && cps.length > 0 && (
            <div className="flex flex-col gap-3" data-testid="lead-list">
              {cps.map((c, i) => {
                const hasReminder = reminders.some((r) => r.cp_id === c.id);
                const bestInvite = (c.invites || []).slice().sort((a, b) => (b.progress_percent || 0) - (a.progress_percent || 0))[0];
                return (
                  <button key={c.id} onClick={() => { setLeadIdx(i); setView("lead"); }}
                    data-testid={`lead-card-${c.id}`}
                    className="text-left glass-strong rounded-2xl p-4 hover:border-[#FF5E1A] border border-transparent transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-bold text-[15px] uppercase tracking-wide text-[#0F172A] truncate">{c.name}</div>
                      <span className="shrink-0 rounded-full bg-white border border-slate-200 px-2.5 py-0.5 text-[10px] font-semibold text-slate-500">
                        {c.company ? "Organization" : "Individual"}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500 flex-wrap">
                      {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                      {(c.address || c.city) && <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" /> {[c.address, c.city].filter(Boolean).join(", ")}</span>}
                    </div>
                    <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.lead_stage && c.lead_stage !== "New" ? "bg-[#EEF4FF] text-[#2A5CB8]" : "bg-slate-100 text-slate-600"}`}>{c.lead_stage || "New"}</span>
                      {c.call_status && <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold">{c.call_status}</span>}
                      {hasReminder && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#B45309]"><Clock className="w-3 h-3" /> reminder set</span>}
                      {bestInvite && (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${bestInvite.status === "completed" ? "bg-emerald-50 text-emerald-700" : bestInvite.status === "in_progress" ? "bg-[#FFF3ED] text-[#FF5E1A]" : "bg-slate-100 text-slate-500"}`}>
                          link {bestInvite.status === "not_opened" ? "sent" : bestInvite.status === "completed" ? "done" : `${bestInvite.progress_percent}%`}
                          {bestInvite.viewers_opened > 0 && ` · ${bestInvite.viewers_opened} opened`}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Stage 2: single-lead calling view */}
          {view === "lead" && cp && (
            <div className="glass-strong rounded-3xl p-5 sm:p-6" data-testid="lead-card">
              <button onClick={() => setView("list")} data-testid="lead-back"
                className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#FF5E1A] font-semibold">
                <ArrowLeft className="w-3.5 h-3.5" /> All leads
              </button>
              {/* Lead header */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-heading text-2xl font-bold text-[#0F172A]" data-testid="lead-name">{cp.name}</h2>
                    <span className="rounded-full bg-white border border-slate-200 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600" data-testid="lead-tag">
                      {cp.company || "Individual"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1" data-testid="lead-counter">Lead {leadIdx + 1} of {cps.length}</div>
                </div>
                <select value={leadIdx} onChange={(e) => setLeadIdx(Number(e.target.value))} data-testid="lead-jump"
                  className="px-2.5 py-1.5 rounded-xl glass-input text-xs max-w-[160px]">
                  {cps.map((c, i) => <option key={c.id} value={i}>{`${i + 1}. ${c.name}`}</option>)}
                </select>
              </div>

              {/* Stage + call status */}
              <div className="grid grid-cols-2 gap-3 mt-5">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Lead stage</div>
                  <select value={stageSel} onChange={(e) => setStageSel(e.target.value)} data-testid="lead-stage-select"
                    className="w-full px-3 py-2 rounded-xl glass-input text-sm">
                    {LEAD_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Call status</div>
                  <select value={statusSel} onChange={(e) => setStatusSel(e.target.value)} data-testid="call-status-select"
                    className="w-full px-3 py-2 rounded-xl glass-input text-sm">
                    {CALL_STATUSES.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
                  </select>
                </div>
              </div>

              {/* Notes + update */}
              <div className="mt-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Call / meeting notes</div>
                <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Enter note here…"
                  data-testid="lead-note-input" className="w-full px-3 py-2 rounded-xl glass-input text-sm min-h-[70px]" />
                <button onClick={updateStatus} data-testid="lead-update-status"
                  className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[#0F172A] text-white px-4 py-2.5 text-sm font-semibold">
                  <Save className="w-4 h-4" /> {savedFlash ? "Saved!" : "Update Status"}
                </button>
              </div>

              {/* Follow-up reminder */}
              <div className="mt-4 rounded-2xl bg-[#FFFDF5] border border-[#F5E6C8] p-4">
                <div className="flex items-center gap-2 text-[#B45309] text-xs font-bold mb-2"><Bell className="w-3.5 h-3.5" /> Follow-up reminder</div>
                <input type="datetime-local" value={remAt} onChange={(e) => setRemAt(e.target.value)}
                  data-testid="lead-remind-at" className="w-full px-3 py-2 rounded-xl glass-input text-sm mb-2" />
                <input type="text" value={remNote} onChange={(e) => setRemNote(e.target.value)} placeholder="Reminder note (optional)"
                  data-testid="lead-remind-note" className="w-full px-3 py-2 rounded-xl glass-input text-sm mb-2" />
                <button onClick={saveReminder} disabled={!remAt} data-testid="lead-remind-save"
                  className="w-full rounded-xl bg-white border border-slate-300 hover:border-[#FF5E1A] px-3 py-2 text-sm font-semibold disabled:opacity-40">
                  Set Reminder
                </button>
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-5 border-t border-slate-100 pt-4" data-testid="lead-details">
                <Field label="RERA / Cert" value={cp.rera} />
                <Field label="Mobile" value={cp.phone} />
                <Field label="Address" value={cp.address} span />
                <Field label="Pincode" value={cp.pincode} />
                <Field label="Registered" value={cp.registered_at} />
                <Field label="City" value={cp.city} />
                <Field label="Team size" value={cp.team_size} />
                <Field label="Email" value={cp.email} span />
              </div>

              {/* Onboarding links */}
              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Onboarding links</div>
                {cp.invites?.length > 0 && (
                  <div className="flex flex-col gap-2 mb-3">
                    {cp.invites.map((inv) => (
                      <div key={inv.id} className="rounded-xl bg-white/70 p-3 border border-slate-100" data-testid={`lead-invite-${inv.id}`}>
                        <div className="flex flex-wrap items-center gap-3 justify-between">
                          <div className="text-xs">
                            <div className="font-semibold">{inv.program_title}</div>
                            <div className="text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                              <StatusPill status={inv.status} />
                              <span>{inv.progress_percent}% · {inv.completed_count}/{inv.total_steps}</span>
                              {inv.last_seen_at && <span>· seen {new Date(inv.last_seen_at).toLocaleDateString()}</span>}
                            </div>
                            {inv.chosen_slot && (
                              <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#EEF4FF] text-[#2A5CB8] px-2 py-0.5 text-[11px] font-semibold" data-testid={`lead-slot-${inv.id}`}>
                                CP chose: {formatSlotIst(inv.chosen_slot)}
                              </div>
                            )}
                            {inv.viewers?.length > 0 && (
                              <div className="mt-1.5 flex flex-col gap-0.5" data-testid={`lead-viewers-${inv.id}`}>
                                {inv.viewers.map((v) => (
                                  <div key={v.id} className="text-[11px] text-slate-600 flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold">{v.name}</span>
                                    <span className={v.completed ? "text-emerald-600 font-semibold" : "text-slate-500"}>
                                      {v.completed ? "Completed" : `${v.progress_percent}%`}
                                    </span>
                                    {v.chosen_slot && <span className="text-[#2A5CB8]">· {formatSlotIst(v.chosen_slot)}</span>}
                                    {v.last_seen_at && <span className="text-slate-400">· seen {new Date(v.last_seen_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <CopyBtn url={buildOnboardUrl(inv)} testid={`invite-copy-${inv.id}`} />
                            <a href={buildOnboardWhatsappHref(inv, cp)} target="_blank" rel="noopener noreferrer"
                              data-testid={`invite-whatsapp-${inv.id}`} title="Share on WhatsApp"
                              className="p-2 rounded-lg text-emerald-600 hover:bg-[#25D366] hover:text-white">
                              <Share2 className="w-4 h-4" />
                            </a>
                            <a href={buildOnboardUrl(inv)} target="_blank" rel="noopener noreferrer"
                              title="Preview link" className="p-2 rounded-lg text-slate-500 hover:text-[#2A5CB8] hover:bg-white">
                              <PlayCircle className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 flex-wrap items-center">
                  <select value={inviteProg} onChange={(e) => setInviteProg(e.target.value)} data-testid="lead-invite-program"
                    className="px-3 py-2 rounded-xl glass-input text-xs flex-1 min-w-[160px]">
                    {programs.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                  <button onClick={() => createInvite(cp.id, inviteProg)} disabled={!inviteProg}
                    data-testid="lead-create-invite"
                    className="rounded-full bg-white border border-slate-300 hover:border-[#FF5E1A] text-xs font-semibold px-3 py-2 disabled:opacity-40">
                    + Create link
                  </button>
                  <button onClick={openComposer} disabled={templates.length === 0}
                    data-testid="lead-send-message"
                    className="rounded-full bg-[#FF5E1A] hover:bg-[#E04F00] text-white text-xs font-semibold px-3 py-2 inline-flex items-center gap-1 disabled:opacity-40">
                    <Send className="w-3.5 h-3.5" /> Send message
                  </button>
                </div>
                {programs.length === 0 && <span className="text-xs text-slate-400 italic">No active programs yet — ask admin.</span>}
              </div>

              {/* Previous notes */}
              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Previous notes / customer story</div>
                <div className="flex flex-col gap-2 max-h-48 overflow-y-auto" data-testid="lead-notes-list">
                  {notes.length === 0 && <p className="text-xs text-slate-400">No notes yet.</p>}
                  {notes.map((n) => (
                    <div key={n.id} className="rounded-xl bg-slate-50 p-3 text-sm">
                      <div className="text-slate-800">{n.text}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action bar */}
              <div className="mt-6 grid grid-cols-6 gap-2" data-testid="lead-action-bar">
                <a href={cp.phone ? `tel:${cp.phone}` : undefined} data-testid={`cp-call-${cp.id}`}
                  className={`col-span-1 rounded-2xl py-3 flex flex-col items-center gap-1 text-xs font-semibold ${cp.phone ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400 pointer-events-none"}`}>
                  <Phone className="w-4 h-4" /> Call
                </a>
                <a href={cp.email ? `mailto:${cp.email}` : undefined} data-testid={`cp-email-${cp.id}`}
                  className={`col-span-1 rounded-2xl py-3 flex flex-col items-center gap-1 text-xs font-semibold ${cp.email ? "bg-[#2A5CB8] text-white" : "bg-slate-100 text-slate-400 pointer-events-none"}`}>
                  <Mail className="w-4 h-4" /> Email
                </a>
                <a href={cp.phone ? `sms:${cp.phone}` : undefined} data-testid={`cp-sms-${cp.id}`}
                  className={`col-span-1 rounded-2xl py-3 flex flex-col items-center gap-1 text-xs font-semibold ${cp.phone ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-400 pointer-events-none"}`}>
                  <MessageSquare className="w-4 h-4" /> SMS
                </a>
                <a href={digits ? `https://wa.me/${digits}` : undefined} target="_blank" rel="noopener noreferrer" data-testid={`cp-whatsapp-${cp.id}`}
                  className={`col-span-1 rounded-2xl py-3 flex flex-col items-center gap-1 text-xs font-semibold ${digits ? "bg-[#25D366] text-white" : "bg-slate-100 text-slate-400 pointer-events-none"}`}>
                  <Share2 className="w-4 h-4" /> WhatsApp
                </a>
                <button onClick={() => setLeadIdx((leadIdx - 1 + cps.length) % cps.length)} data-testid="lead-prev"
                  className="col-span-1 rounded-2xl py-3 bg-white border border-slate-300 flex items-center justify-center" title="Previous lead">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setLeadIdx((leadIdx + 1) % cps.length)} data-testid="lead-next"
                  className="col-span-1 rounded-2xl py-3 bg-[#0F172A] text-white flex flex-col items-center gap-1 text-xs font-semibold">
                  <ChevronRight className="w-4 h-4" /> Next
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Template message composer */}
      {msgOpen && cp && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center px-4 py-6 overflow-y-auto" onClick={() => setMsgOpen(false)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-5 shadow-2xl my-6" onClick={(e) => e.stopPropagation()} data-testid="msg-composer">
            <h3 className="font-heading font-bold text-lg mb-3">Send message · {cp.name}</h3>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Training / program</label>
            <select value={msgProg} onChange={(e) => setMsgProg(e.target.value)} data-testid="msg-program"
              className="w-full mt-1 mb-2 px-3 py-2 rounded-xl glass-input text-sm">
              {programs.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Template</label>
            <select value={msgTpl} onChange={(e) => setMsgTpl(e.target.value)} data-testid="msg-template"
              className="w-full mt-1 mb-2 px-3 py-2 rounded-xl glass-input text-sm">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Message (editable)</label>
            <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)}
              data-testid="msg-preview" className="w-full mt-1 px-3 py-2 rounded-xl glass-input text-sm min-h-[140px]" />
            <div className="flex gap-2 mt-3">
              <button onClick={async () => { try { await navigator.clipboard.writeText(msgText); } catch {}; setMsgCopied(true); setTimeout(() => setMsgCopied(false), 1500); }}
                data-testid="msg-copy"
                className="flex-1 rounded-xl bg-white border border-slate-300 px-3 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-1">
                {msgCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {msgCopied ? "Copied" : "Copy"}
              </button>
              <a href={digits ? `https://wa.me/${digits}?text=${encodeURIComponent(msgText)}` : undefined} target="_blank" rel="noopener noreferrer"
                data-testid="msg-whatsapp"
                className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-1 ${digits ? "bg-[#25D366] text-white" : "bg-slate-100 text-slate-400 pointer-events-none"}`}>
                <Share2 className="w-4 h-4" /> WhatsApp
              </a>
            </div>
            <button onClick={() => setMsgOpen(false)} className="mt-3 w-full text-xs text-slate-500 hover:text-[#FF5E1A]">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
